import { useEffect, useState } from 'react';

import { getTicketConfig, redeemSession } from '../services/ticket-api';
import { parseTicketHash, scrubTicketHashFromUrl } from '../utils';

const INVALID_HASH_ERROR = {
  message: 'Invalid or missing session link.',
  code: 'INVALID_HASH'
};

const mapBootstrapError = error => {
  if (!error || typeof error.status !== 'number') {
    return { message: 'Unable to load form.', code: 'UNKNOWN' };
  }

  if (error.status === 410) {
    return {
      message: 'Session code expired — open a new ticket from Communicator.',
      code: error.code
    };
  }

  if (error.status === 401) {
    return {
      message: 'This link has already been used.',
      code: error.code
    };
  }

  if (error.status === 403) {
    return {
      message: error.description || 'Invalid ticket link.',
      code: error.code
    };
  }

  if (error.status === 503) {
    return {
      message: 'Unable to load form. Please try again later.',
      code: error.code
    };
  }

  return {
    message: error.description || 'Unable to load form.',
    code: error.code
  };
};

export const useTicketBootstrap = () => {
  const [state, setState] = useState({
    status: 'parsing',
    config: null,
    eid: null,
    error: null
  });

  useEffect(() => {
    const parsed = parseTicketHash();
    scrubTicketHashFromUrl();

    if (!parsed) {
      setState({
        status: 'error',
        config: null,
        eid: null,
        error: INVALID_HASH_ERROR
      });

      return undefined;
    }

    const { code, eid } = parsed;
    let cancelled = false;

    const bootstrap = async () => {
      setState({ status: 'redeeming', config: null, eid, error: null });

      try {
        await redeemSession(eid, code);
        if (cancelled) {
          return;
        }

        setState({ status: 'loadingConfig', config: null, eid, error: null });
        const config = await getTicketConfig(eid);
        if (cancelled) {
          return;
        }

        setState({ status: 'ready', config, eid, error: null });
      } catch (error) {
        if (!cancelled) {
          setState({
            status: 'error',
            config: null,
            eid,
            error: mapBootstrapError(error)
          });
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};
